import React, { useEffect, useState } from "react"
import { getAPIBaseURL, getUser } from "../services/helpers"
import { updateData } from "../services/request"
import { getApiErrorMessage, getMessageFromResponseData } from "../services/apiErrorMessage"
import { validateAddress, validateEmail, validateName, validatePhoneNumber, validateCountry } from "../services/validators"
import { countries } from "../data"

const PatientProfile = () => {
  const user = getUser()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [country, setCountry] = useState("NG")
  const [address, setAddress] = useState("")
  const [btnState, setBtnState] = useState("Update Profile")
  const [error, setError] = useState([
    { field: "name", msg: "" },
    { field: "email", msg: "" },
    { field: "phoneNumber", msg: "" },
    { field: "country", msg: "" },
    { field: "address", msg: "" }
  ])
  const [genError, setGenError] = useState("")
  const [genMsg, setGenMsg] = useState("")
  const [loadingCountries] = useState(false)

  useEffect(() => {
    if (user) {
      setName(user.name || "")
      setEmail(user.email || "")
      setPhoneNumber(user.phoneNumber || "")
      setCountry(user.country || "NG")
      setAddress(user.address || "")
    }
  }, [user])

  const validateForm = () => {
    let isValid = true
    const newErrors = [...error]

    const nameValidation = validateName(name)
    newErrors[newErrors.findIndex((e) => e.field === "name")].msg = nameValidation.result
    if (nameValidation.error !== "") isValid = false

    const emailValidation = validateEmail(email)
    newErrors[newErrors.findIndex((e) => e.field === "email")].msg = emailValidation.result
    if (emailValidation.error !== "") isValid = false

    const phoneValidation = validatePhoneNumber(phoneNumber)
    newErrors[newErrors.findIndex((e) => e.field === "phoneNumber")].msg = phoneValidation.result
    if (phoneValidation.error !== "") isValid = false

    const countryValidation = validateCountry(country)
    newErrors[newErrors.findIndex((e) => e.field === "country")].msg = countryValidation.result
    if (countryValidation.error !== "") isValid = false

    const addressValidation = validateAddress(address)
    newErrors[newErrors.findIndex((e) => e.field === "address")].msg = addressValidation.result
    if (addressValidation.error !== "") isValid = false

    setError(newErrors)
    return isValid
  }

  const updateProfile = async () => {
    setGenError("")
    setGenMsg("")
    if (!validateForm()) {
      return
    }

    setBtnState("Updating...")
    const url = `${getAPIBaseURL()}/v1/users/update-account`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('name', name)
    params.append('email', email)
    params.append('phoneNumber', phoneNumber)
    params.append('country', country)
    params.append('address', address)

    const res = await updateData(url, headers, params.toString())

    setBtnState("Update Profile")
    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg(getMessageFromResponseData(res.result.data) || 'Profile updated successfully')
        if (res.result.data?.data) {
          localStorage.setItem('user', JSON.stringify(res.result.data.data))
        }
      } else {
        setGenError(getApiErrorMessage(res, 'Update failed'))
      }
    } else {
      setGenError(getApiErrorMessage(res, 'Update failed'))
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-3 sm:px-0">
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Patient Profile</h2>
          <p className="mt-1 text-sm text-gray-600">Update your personal information</p>
        </div>

        <div className="p-4 sm:p-8">
          <div className="space-y-6">
            {genMsg ? (
              <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm font-medium text-green-700">{genMsg}</div>
            ) : null}
            {genError ? (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm font-medium text-red-700">{genError}</div>
            ) : null}

            <div>
              <label htmlFor="name" className="mb-2 block text-sm font-semibold text-gray-900">Full name</label>
              <input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="John Doe"
              />
              {error.find((item) => item.field === "name")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find((item) => item.field === "name").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-semibold text-gray-900">Email address</label>
              <input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="name@example.com"
              />
              {error.find((item) => item.field === "email")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find((item) => item.field === "email").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="phoneNumber" className="mb-2 block text-sm font-semibold text-gray-900">Phone number</label>
              <input
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                type="tel"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="08012345678"
              />
              {error.find((item) => item.field === "phoneNumber")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find((item) => item.field === "phoneNumber").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="country" className="mb-2 block text-sm font-semibold text-gray-900">Country</label>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
              >
                <option value="">{loadingCountries ? 'Loading countries...' : 'Select Country'}</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
              {error.find((item) => item.field === "country")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find((item) => item.field === "country").msg}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="address" className="mb-2 block text-sm font-semibold text-gray-900">Address</label>
              <textarea
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows="4"
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0 resize-none"
                placeholder="Enter your full address"
              />
              {error.find((item) => item.field === "address")?.msg ? (
                <p className="mt-1.5 text-sm font-medium text-red-600">{error.find((item) => item.field === "address").msg}</p>
              ) : null}
            </div>

            <div className="pt-2">
              <button
                onClick={updateProfile}
                type="button"
                className="w-full rounded-lg bg-black px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                {btnState}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatientProfile


